import { type DataRoom } from "@decentriq/core";
import { type PublishedMediaDataRoomQuery } from "@decentriq/graphql/dist/types";
import {
  faBullhorn,
  faEye,
  faGear,
  faNewspaper,
  type IconDefinition,
} from "@fortawesome/pro-light-svg-icons";
import { type data_room as ddcDataRoom } from "ddc";
import { type ReactElement } from "react";
import HandHoldingDocIcon from "../assets/hand-holding-doc.svg";
import PeopleIcon from "../assets/people.svg";

export enum MediaDataRoomVersion {
  ab = "ab",
  v3 = "v3",
}

export type MediaDataRoomAb =
  PublishedMediaDataRoomQuery["publishedMediaDataRoom"];
export type MediaDataRoomV3 = DataRoom & { driverAttestationHash: string };

export enum MediaDataRoomParticipantRole {
  Publisher = "PUBLISHER",
  Advertiser = "ADVERTISER",
  Observer = "OBSERVER",
  Agency = "AGENCY",
  DataPartner = "DATA_PARTNER",
  Custom = "CUSTOM",
}

export const mediaDataRoomOrganizationRolePresentationMap: Record<
  MediaDataRoomParticipantRole,
  string
> = {
  [MediaDataRoomParticipantRole.Advertiser]: "Advertiser",
  [MediaDataRoomParticipantRole.Publisher]: "Publisher",
  [MediaDataRoomParticipantRole.DataPartner]: "Data partner",
  [MediaDataRoomParticipantRole.Agency]: "Agency",
  [MediaDataRoomParticipantRole.Observer]: "Observer",
  [MediaDataRoomParticipantRole.Custom]: "Custom",
};

export type MediaDataRoomParticipantPermission = ddcDataRoom.Permission;

export interface MediaDataRoomParticipant {
  id: string;
  role: MediaDataRoomParticipantRole;
  name: string;
  logo?: string;
  isCurrentUser: boolean;
  permissions: MediaDataRoomParticipantPermission[];
  emails: string[];
}

export const mediaDataRoomOrganizationPermissionsMap: Record<
  MediaDataRoomParticipantPermission,
  string
> = {
  CREATE_CUSTOM_AUDIENCE: "Create audience",
  EXPORT_AUDIENCE: "Export audience",
  MODEL_QUALITY: "Model quality",
  PROVIDE_BASE_AUDIENCE: "Provide base audience",
  PROVIDE_SEED_AUDIENCE: "Provide seed audience",
  VIEW_INSIGHTS: "View insights",
  VIEW_OVERLAP: "View overlap",
};

export interface MediaDataRoomOrganizationRoleCardPresentationType {
  title: string;
  description: string;
  icon: IconDefinition | ReactElement;
}

export const mediaDataRoomParticipantRoleCardPresentation: Record<
  MediaDataRoomParticipantRole,
  MediaDataRoomOrganizationRoleCardPresentationType
> = {
  [MediaDataRoomParticipantRole.Advertiser]: {
    description:
      "By default, advertisers provide the seed audience, see insights and can create audiences.",
    icon: faBullhorn,
    title:
      mediaDataRoomOrganizationRolePresentationMap[
        MediaDataRoomParticipantRole.Advertiser
      ],
  },
  [MediaDataRoomParticipantRole.Publisher]: {
    description:
      "Publishers can be selected from the Decentriq network or can be invited directly. By default, publishers provide the base audience, can see insights and export audiences made available to them.",
    icon: faNewspaper,
    title:
      mediaDataRoomOrganizationRolePresentationMap[
        MediaDataRoomParticipantRole.Publisher
      ],
  },
  [MediaDataRoomParticipantRole.DataPartner]: {
    description:
      "Data partners can be selected from the Decentriq network or can be invited directly. By default, data partners provide the seed audience.",
    icon: <HandHoldingDocIcon />,
    title:
      mediaDataRoomOrganizationRolePresentationMap[
        MediaDataRoomParticipantRole.DataPartner
      ],
  },
  [MediaDataRoomParticipantRole.Agency]: {
    description:
      "Agency can be selected from Decentriq's agency portal. By default, agencies provide the seed audience.",
    icon: <PeopleIcon />,
    title:
      mediaDataRoomOrganizationRolePresentationMap[
        MediaDataRoomParticipantRole.Agency
      ],
  },
  [MediaDataRoomParticipantRole.Observer]: {
    description:
      "Observer can be selected from Decentriq's observer portal. By default, observers can see insights.",
    icon: faEye,
    title:
      mediaDataRoomOrganizationRolePresentationMap[
        MediaDataRoomParticipantRole.Observer
      ],
  },
  [MediaDataRoomParticipantRole.Custom]: {
    description: "",
    icon: faGear,
    title:
      mediaDataRoomOrganizationRolePresentationMap[
        MediaDataRoomParticipantRole.Custom
      ],
  },
};

export enum RawSupportedFeatures {
  ENABLE_MODEL_PERFORMANCE_EVALUATION = "ENABLE_MODEL_PERFORMANCE_EVALUATION",
  ENABLE_HIDE_ABSOLUTE_AUDIENCE_SIZES = "ENABLE_HIDE_ABSOLUTE_AUDIENCE_SIZES",
  ENABLE_ADVERTISER_AUDIENCE_DOWNLOAD = "ENABLE_ADVERTISER_AUDIENCE_DOWNLOAD",
}

export const PERFORMANCE_REPORT_ALLOWED_USERS = ["model.quality@decentriq.com"];
